import { get, post, put } from "aws-amplify/api";
import { default as React, useState, useEffect, useCallback, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { PacmanLoader } from "react-spinners";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import "containers/common/List.css";
import "containers/common/Table.css";
import "./UserList.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function UserList({ isAuthenticated, user }) {
  const userIdPrefix = "user-list";

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations,] = useState([
    { value: "onshore", label: "Onshore" },
    { value: "offshore", label: "Offshore" },
  ]);
  const [stateSelectedLocation, setSelectedLocation] = useState("");
  const [location, setLocation] = useState("");
  const [trustproof, setTrustProof] = useState(false);

  // --- API Functions ---
  const getUsers = useCallback(async (filter = {}) => {
    // console.log("Inside getUsers() ");

    try {
      const response = await get({
        apiName: "colin",
        path: "/users",
        options: {
          queryParams: filter,
        },
      }).response;

      const cs = await response.body.json();
      return cs;
    } catch (error) {
      console.error("API Error", error);
      throw error;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    const loadData = async () => {
      setIsLoading(true);
      try {
        const localusers = await getUsers();
        setUsers(localusers || []);
      } catch (e) {
        console.error("Load Error:", e);
      }
      setIsLoading(false);
    };

    loadData();
  }, [isAuthenticated, user, getUsers]);

  const saveRecord = useCallback(async (record) => {
    const isNew = record.new_record;
    const path = isNew ? "/users/new" : `/users/${record["user_name"]}`;
    const method = isNew ? post : put;

    try {
      const result = await method({
        apiName: "colin",
        path: path,
        options: { body: { ...record, last_edited_by: user } }
      });
      return await result.response;
    } catch (err) {
      console.error("Save error:", err);
      throw err;
    }
  }, [user]);

  // --- Table Logic ---
  const updateTableState = (rowIndex, columnId, value) => {
    setUsers(old => old.map((record, index) => (
      index === rowIndex
        ? { ...record, [columnId]: value, modified: true }
        : record
    )));
  };

  // --- Save Logic Functions ---
  const handleSave = async ({ row, column, value }) => {
    const newValue = column.id === "trust_proof" ? Boolean(value) : value;
    const recordToSave = { ...row.original, [column.id]: newValue };

    // Validation
    if (recordToSave.new_record && (!recordToSave.user_name || recordToSave.user_name.trim() === "")) {
      return;
    }

    if (recordToSave.isSaving) return;

    setUsers(old => old.map(r =>
      r === row.original ? { ...r, isSaving: true, [column.id]: newValue } : r
    ));

    try {
      await saveRecord(recordToSave);

      setUsers(old => old.map(r =>
        r === row.original
          ? { ...r, [column.id]: newValue, modified: false, isSaving: false, new_record: false }
          : r
      ));
    } catch (err) {
      console.error("Save failed:", err);
      setUsers(old => old.map(r => r === row.original ? { ...r, isSaving: false } : r));
      setUi({ showAlert: true, errorMessage: "Network Error: Could not save user." });
    }
  };

  const addRecord = () => {
    setUsers(prev => [{
      user_name: "",
      locations: "offshore",
      trust_proof: false,
      created_by: user,
      last_edited_by: user,
      new_record: true,
    }, ...prev]);
  };

  const handleCheckboxChange = (e) => {
    setTrustProof(e.target.checked);
  };

  const handleDeleteRecord = useCallback(async (props, event) => {
    if (event) event.preventDefault();
    const recordToDelete = props.row.original;

    if (window.confirm(`Are you sure you want to delete ${recordToDelete.user_name}?`)) {
      const updatedRecord = {
        ...recordToDelete,
        archived_date: new Date().toISOString(),
      };

      try {
        await saveRecord(updatedRecord);
        setUsers(prev => prev.filter(u => u.user_name !== recordToDelete.user_name));
      } catch (err) {
        console.error("Delete failed", err);
        setUi({ showAlert: true, errorMessage: "Failed to delete user." });
      }
    }
  }, [saveRecord]);

  const handleLocationChange = (selectedLocation) => {
    if (selectedLocation) {
      setSelectedLocation(selectedLocation);
      setLocation(selectedLocation.value);
    } else {
      setSelectedLocation({ value: "", label: "" });
      setLocation("");
    }
  };

  async function handleSubmit(event) {
    event.preventDefault();
    setIsLoading(true);

    try {
      const filter = {};

      if (location) {
        filter.location = location;
      }

      filter.trustproof = trustproof;

      const localusers = await getUsers(filter);
      setUsers(localusers || []);
    } catch (e) {
      console.error("Search Error:", e);
      setUi({ showAlert: true, errorMessage: "Failed to search users." });
    }

    setIsLoading(false);
  }

  const createId = useMemo(
    () => createElementIdFactory(userIdPrefix),
    [userIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  // --- Column Definitions ---
  const columns = useMemo(() => [
    columnHelper.accessor("user_name", {
      id: "user_name",
      header: "User Name",
      cell: EditableCell,
      size: 40,
    }),
    columnHelper.accessor("locations", {
      id: "locations",
      header: "Location",
      cell: EditableCell,
      size: 40,
    }),
    columnHelper.accessor("trust_proof", {
      id: "trust_proof",
      header: "Trust Proof",
      cell: EditableCell,
      size: 20,
      meta: {
        type: "checkbox",
        align: "center",
      }
    }),
    columnHelper.accessor("created_by", {
      id: "created_by",
      header: "Created By",
      cell: (info) => info.getValue(),
      size: 40,
    }),
    columnHelper.accessor("last_edited_by", {
      id: "last_edited_by",
      header: "Last Edited By",
      cell: (info) => info.getValue(),
      size: 40,
    }),
    columnHelper.accessor("user_name", {
      id: "user_name2",
      header: " ",
      cell: (props) => (
        <Button id={createId("delete", "button", props.row.id)} type="button" size="sm" variant="danger" onClick={(e) => handleDeleteRecord(props, e)}>Delete</Button>
      ),
      size: 300,
      enableSorting: false,
    }),
  ], [createId, handleDeleteRecord]);

  if (!isAuthenticated) return null;

  // --- Render Functions ---
  return (
    <div className="mt-3">
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>

      <Form id={createId("search", "form")} onSubmit={handleSubmit}>
        <Row>
          <Col lg={2}>
            <Form.Group controlId="location">
              <Select
                value={stateSelectedLocation}
                onChange={handleLocationChange}
                options={locations}
                placeholder="Location"
                isClearable={true}
                inputId={createId("location", "input")}
                instanceId={createId("location", "instance")}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </Form.Group>
          </Col>

          <Col lg={2}>
            <Form.Group controlId="trustproof" className="user-list-trustproof-toggle">
              <Form.Check
                id={createId("trustproof", "checkbox")}
                className="user-list-trustproof-check"
                checked={trustproof}
                onChange={handleCheckboxChange}
                label="Trust Proof"
                type="checkbox"
                name="trustproof"
              />
            </Form.Group>
          </Col>
          <Col lg={2}>
            <Button id={createId("search", "button")} type="submit" className="float-left">Search</Button>
          </Col>
        </Row>
      </Form>

      <Col className="text-right" style={{ marginBottom: '12px' }}>
        <Button id={createId("add", "button")} variant="secondary" size="sm" onClick={addRecord}>Add</Button>
      </Col>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={users}
                section="user-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.userList}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                meta={{
                  updateData: updateTableState,
                  handleSave: handleSave,
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default UserList;